import React, { useEffect } from 'react';
import {
  Title,
  Text,
  Label,
  Select,
  Option,
  Button,
  MessageStrip,
  Switch,
  Icon,
} from '@ui5/webcomponents-react';
import useAutoTranslate from '../../../hooks/useAutoTranslate';
import { CONFIG_STYLES } from '../configStyles';

function RegionalConfig({ formData, setFormData, systemSettings, onSave, isLoading }) {
  const regionalSettings = systemSettings?.REGIONAL;
  const { autoT } = useAutoTranslate();

  useEffect(() => {
    console.log('RegionalConfig - Current Values:', { formData, regionalSettings });
  }, [formData, regionalSettings]);

  if (!formData || !regionalSettings) {
    return <div>Error: Faltan datos necesarios</div>;
  }

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const prepareDataForSave = () => ({
    ...regionalSettings,
    currency:           { ...regionalSettings.currency,           default: formData.currency },
    timezone:           { ...regionalSettings.timezone,           default: formData.timeZone },
    dateFormat:         { ...regionalSettings.dateFormat,         default: formData.dateFormat },
    timeFormat:         { ...regionalSettings.timeFormat,         default: formData.timeFormat },
    decimalSeparator:   { ...regionalSettings.decimalSeparator,   default: formData.decimalSeparator },
    thousandsSeparator: { ...regionalSettings.thousandsSeparator, default: formData.thousandsSeparator },
    decimalPlaces:      { ...regionalSettings.decimalPlaces,      default: formData.decimalPlaces },
    alwaysShowDecimals: { ...regionalSettings.alwaysShowDecimals, default: formData.alwaysShowDecimals },
  });

  const handleSave = () => onSave(prepareDataForSave());

  const hasChanges = () => {
    return (
      formData.currency           !== regionalSettings.currency.default           ||
      formData.timeZone           !== regionalSettings.timezone.default           ||
      formData.dateFormat         !== regionalSettings.dateFormat.default         ||
      formData.timeFormat         !== regionalSettings.timeFormat.default         ||
      formData.decimalSeparator   !== regionalSettings.decimalSeparator.default   ||
      formData.thousandsSeparator !== regionalSettings.thousandsSeparator.default ||
      String(formData.decimalPlaces) !== String(regionalSettings.decimalPlaces.default) ||
      formData.alwaysShowDecimals !== regionalSettings.alwaysShowDecimals.default
    );
  };

  const renderSelect = (field, options, currentValue) => (
    <Select
      key={currentValue || ''}
      style={{ width: '100%' }}
      onChange={(e) => {
        const selectedValue =
          e.detail.selectedOption.getAttribute('data-key') || e.detail.selectedOption.value;
        handleInputChange(field, selectedValue);
      }}
    >
      {options.map(option => (
        <Option
          key={option.value}
          data-key={option.value}
          selected={String(option.value) === String(currentValue)}
        >
          {option.label}
        </Option>
      ))}
    </Select>
  );

  return (
    <div style={CONFIG_STYLES.page}>
      {/* Header */}
      <div style={CONFIG_STYLES.headerRow}>
        <div style={CONFIG_STYLES.headerTitle}>
          <Title level="H3">{autoT('Configuración Regional')}</Title>
          <Text style={CONFIG_STYLES.labelHint}>{autoT('Formato de moneda, fechas y números')}</Text>
        </div>
        <Button
          design="Emphasized"
          onClick={handleSave}
          disabled={!hasChanges() || isLoading}
          icon="save"
        >
          {isLoading ? autoT('Guardando...') : autoT('Guardar Cambios')}
        </Button>
      </div>

      {hasChanges() && (
        <MessageStrip design="Information" style={{ marginBottom: '1rem' }}>
          {autoT('Hay cambios pendientes de guardar')}
        </MessageStrip>
      )}

      {/* Formatos */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Moneda y Zona Horaria')}</Title>
        </div>
        <div style={CONFIG_STYLES.formGrid}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Moneda Principal')}</Label>
            {renderSelect('currency', regionalSettings.currency.options, formData.currency)}
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Valor actual')}: {formData.currency}</Text>
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Zona Horaria')}</Label>
            {renderSelect('timeZone', regionalSettings.timezone.options, formData.timeZone)}
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Valor actual')}: {formData.timeZone}</Text>
          </div>
        </div>
      </div>

      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Formato de Fecha y Hora')}</Title>
        </div>
        <div style={CONFIG_STYLES.formGrid}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Formato de Fecha')}</Label>
            {renderSelect('dateFormat', regionalSettings.dateFormat.options, formData.dateFormat)}
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Valor actual')}: {formData.dateFormat}</Text>
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Formato de Hora')}</Label>
            {renderSelect('timeFormat', regionalSettings.timeFormat.options, formData.timeFormat)}
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Valor actual')}: {formData.timeFormat}</Text>
          </div>
        </div>
      </div>

      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Formato Numérico')}</Title>
        </div>
        <div style={CONFIG_STYLES.formGrid}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Separador Decimal')}</Label>
            {renderSelect('decimalSeparator', regionalSettings.decimalSeparator.options, formData.decimalSeparator)}
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Valor actual')}: {formData.decimalSeparator}</Text>
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Separador de Miles')}</Label>
            {renderSelect('thousandsSeparator', regionalSettings.thousandsSeparator.options, formData.thousandsSeparator)}
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Valor actual')}: {formData.thousandsSeparator}</Text>
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Cantidad de Decimales')}</Label>
            <Select
              key={String(formData.decimalPlaces) || ''}
              style={{ width: '100%' }}
              onChange={(e) => {
                const selectedValue =
                  e.detail.selectedOption.getAttribute('data-key') || e.detail.selectedOption.value;
                handleInputChange('decimalPlaces', selectedValue);
              }}
            >
              {regionalSettings.decimalPlaces.options.map(option => (
                <Option
                  key={option.value}
                  data-key={option.value}
                  selected={String(option.value) === String(formData.decimalPlaces)}
                >
                  {option.label}
                </Option>
              ))}
            </Select>
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Valor actual')}: {formData.decimalPlaces}</Text>
          </div>

          <div style={{ ...CONFIG_STYLES.fieldGroup, justifyContent: 'flex-end' }}>
            <Label>{autoT('Mostrar siempre los decimales')}</Label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
              <Switch
                checked={formData.alwaysShowDecimals}
                onChange={(e) => handleInputChange('alwaysShowDecimals', e.target.checked)}
              />
              <Text style={CONFIG_STYLES.labelHint}>
                {formData.alwaysShowDecimals ? autoT('Activo') : autoT('Inactivo')}
              </Text>
            </div>
          </div>
        </div>
      </div>

      {/* Info */}
      <div style={CONFIG_STYLES.infoBox}>
        <div style={CONFIG_STYLES.infoHeader}>
          <Icon name="information" style={{ color: 'var(--sapHighlightColor)' }} />
          <Title level="H5">{autoT('Información')}</Title>
        </div>
        <ul style={{ color: 'var(--sapContent_LabelColor)', margin: 0, paddingLeft: '1.25rem', lineHeight: '1.8' }}>
          <li><Text>{autoT('Los cambios en el formato regional se aplican a toda la aplicación')}</Text></li>
          <li><Text>{autoT('La moneda principal determina el formato de los valores monetarios')}</Text></li>
          <li><Text>{autoT('El separador decimal y de miles deben ser distintos')}</Text></li>
        </ul>
      </div>
    </div>
  );
}

export default RegionalConfig;
